import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { safeNavigate } from '../utils/navigationUtils';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { BarChart2, Briefcase, Calendar, CheckCircle, Edit, Mail, MapPin, MessageSquare, Plus, Trash2, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import translations from '../contexts/translations';
import proService from '../services/proService';

const ProSection = ({ language }) => {
  const t = translations[language];
  const navigate = useNavigate();
  // const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isProUser, setIsProUser] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('listings');
  const [listings, setListings] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [messages, setMessages] = useState([]);
  const [analytics, setAnalytics] = useState({ totalBookings: 0, revenue: 0, pendingBookings: 0 });
  const [newListing, setNewListing] = useState({
    type: 'hotel',
    title: '',
    description: '',
    price: '',
    location: '',
  });
  const [showCreateForm, setShowCreateForm] = useState(false);
  const fetchProData = React.useCallback(async () => {
    try {
      setLoading(true);
      const [listingsData, bookingsData, messagesData, analyticsData] = await Promise.all([
        proService.getListings(),
        proService.getBookings(),
        proService.getMessages(),
        proService.getAnalytics(),
      ]);
      setListings(listingsData);
      setBookings(bookingsData);
      setMessages(messagesData);
      setAnalytics(analyticsData);
      setLoading(false);
    } catch (err) {
      setError(t.errorFetchingData || 'Error loading professional data');
      toast.error(t.errorFetchingData || 'Error loading professional data');
      setLoading(false);
    }
  }, [t.errorFetchingData]);

  const checkProStatus = React.useCallback(async () => {
    try {
      setLoading(true);
      const data = await proService.getAnalytics(); // Just a sample call to verify pro status
      if (data) {
        setIsProUser(true);
        fetchProData();
      }
    } catch (err) {
      setError(t.errorFetchingProfile || 'Error verifying user status');
      toast.error(t.errorFetchingProfile || 'Error verifying user status');
      setLoading(false);
    }
  }, [t.errorFetchingProfile, fetchProData]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      safeNavigate(navigate, '/login', { state: { from: '/pro' } });
      return;
    }
    checkProStatus();
  }, [checkProStatus, navigate]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setShowCreateForm(false);
  };

  const handleCreateListingChange = (e) => {
    const { name, value } = e.target;
    setNewListing((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateListingSubmit = async (e) => {
    e.preventDefault();
    try {
      await proService.createListing(newListing);
      toast.success(t.listingCreated || 'Listing created successfully');
      setNewListing({ type: 'hotel', title: '', description: '', price: '', location: '' });
      setShowCreateForm(false);
      fetchProData();
    } catch (err) {
      toast.error(t.errorCreatingListing || 'Error creating listing');
    }
  };

  const handlePublishToggle = async (listingId, currentStatus) => {
    try {
      await proService.updateListingStatus(listingId, currentStatus === 'published' ? 'draft' : 'published');
      toast.success(t.listingUpdated || 'Listing status updated');
      fetchProData();
    } catch (err) {
      toast.error(t.errorUpdatingListing || 'Error updating listing');
    }
  };

  const handleDeleteListing = async (listingId) => {
    if (!window.confirm(t.confirmDelete || 'Are you sure you want to delete this listing?')) return;
    try {
      await proService.deleteListing(listingId);
      toast.success(t.listingDeleted || 'Listing deleted successfully');
      fetchProData();
    } catch (err) {
      toast.error(t.errorDeletingListing || 'Error deleting listing');
    }
  };

  const handleBookingStatusUpdate = async (bookingId, newStatus) => {
    try {
      await proService.updateBookingStatus(bookingId, newStatus);
      toast.success(t.bookingUpdated || 'Booking status updated');
      fetchProData();
    } catch (err) {
      toast.error(t.errorUpdatingBooking || 'Error updating booking');
    }
  };

  const handleMarkMessageRead = async (messageId) => {
    try {
      await proService.markMessageRead(messageId);
      toast.success(t.messageMarkedRead || 'Message marked as read');
      fetchProData();
    } catch (err) {
      toast.error(t.errorUpdatingMessage || 'Error updating message');
    }
  };

  const bookingSteps = [
    {
      title: t.createListing || 'Create Listing',
      description: t.createListingDesc || 'Add your hotels, circuits, cars, or tours to attract clients.',
      icon: <Plus className="text-3xl" style={{ color: '#F97316' }} />,
    },
    {
      title: t.manageBookings || 'Manage Bookings',
      description: t.manageBookingsDesc || 'Review and update booking statuses to keep clients informed.',
      icon: <CheckCircle className="text-3xl" style={{ color: '#F97316' }} />,
    },
    {
      title: t.engageClients || 'Engage Clients',
      description: t.engageClientsDesc || 'Respond to client inquiries and build lasting relationships.',
      icon: <MessageSquare className="text-3xl" style={{ color: '#F97316' }} />,
    },
  ];

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: 'linear-gradient(to bottom, #FFF7ED, #F9E9D2 80%)' }}
      >
        <div className="text-amber-900">{t.loading || 'Loading...'}</div>
      </div>
    );
  }

  if (error || !isProUser) {
    return (
      <div
        className="min-h-screen flex items-center justify-center"
        style={{ background: 'linear-gradient(to bottom, #FFF7ED, #F9E9D2 80%)' }}
      >
        <div className="bg-red-100 border border-red-400 text-red-700 px-6 py-4 rounded-xl max-w-md">
          {error || t.notAuthorizedUser || 'Access restricted to authorized users'}
          <button
            onClick={() => navigate('/')}
            className="mt-4 w-full py-2 px-4 bg-orange-600 text-white rounded-xl hover:bg-orange-700 transition-colors"
          >
            {t.backToHome || 'Back to Home'}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen py-24 relative overflow-hidden"
      style={{ background: 'linear-gradient(to bottom, #FFF7ED, #F9E9D2 80%)' }}
    >
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: 'url(/src/assets/zelij3.png)',
          backgroundRepeat: 'repeat',
          backgroundSize: '400px',
          backgroundAttachment: 'fixed',
          opacity: 0.08,
        }}
      ></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,186,73,0.08),transparent_60%)]"></div>
      <div className="container mx-auto px-6 max-w-7xl relative z-10">
        {/* Hero Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl font-serif font-extrabold text-amber-900 mb-4">
            {t.proDashboard || 'Professional Dashboard'}
          </h1>
          <p className="text-lg max-w-2xl mx-auto leading-relaxed text-amber-900/80">
            {t.proDashboardDesc || 'Manage your travel offerings, bookings, and client communications with ease.'}
          </p>
        </motion.div>

        {/* Quick actions + trust */}
        <motion.section
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, delay: 0.1 }}
          className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8"
        >
          <button
            type="button"
            onClick={() => {
              setActiveTab('listings');
              setShowCreateForm(true);
            }}
            className="text-left rounded-2xl border border-amber-200 bg-white/90 p-5 shadow-sm hover:shadow-md transition-shadow"
          >
            <div className="text-xs uppercase tracking-wide text-amber-700/80">Action rapide</div>
            <div className="mt-2 text-lg font-bold text-amber-900">Creer une offre</div>
            <p className="text-sm text-amber-900/80 mt-1">Publiez rapidement un nouvel hebergement, circuit ou service.</p>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('bookings')}
            className="text-left rounded-2xl border border-amber-200 bg-white/90 p-5 shadow-sm hover:shadow-md transition-shadow"
          >
            <div className="text-xs uppercase tracking-wide text-amber-700/80">Pilotage</div>
            <div className="mt-2 text-lg font-bold text-amber-900">Suivre les reservations</div>
            <p className="text-sm text-amber-900/80 mt-1">Visualisez les demandes en attente et validez en un clic.</p>
          </button>

          <div className="rounded-2xl border border-amber-200 bg-white/90 p-5 shadow-sm">
            <div className="text-xs uppercase tracking-wide text-amber-700/80">Fiabilite</div>
            <div className="mt-2 text-lg font-bold text-amber-900">Espace pro securise</div>
            <p className="text-sm text-amber-900/80 mt-1">Donnees protegees et tableaux de bord en temps reel.</p>
          </div>
        </motion.section>

        {/* Tabs */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="flex flex-wrap gap-4 mb-8 border-b border-amber-100"
        >
          {['listings', 'bookings', 'messages', 'analytics'].map((tab) => (
            <button
              key={tab}
              onClick={() => handleTabChange(tab)}
              className={`py-2 px-4 font-semibold transition-colors ${
                activeTab === tab
                  ? 'text-orange-600 border-b-2 border-orange-600'
                  : 'text-amber-900/80 hover:text-orange-600'
              }`}
            >
              {t[tab] || tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </motion.div>

        {/* Listings Tab */}
        {activeTab === 'listings' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-serif font-semibold text-amber-900">{t.listings || 'Your Listings'}</h2>
              <motion.button
                onClick={() => setShowCreateForm(!showCreateForm)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="py-2 px-4 bg-orange-600 text-white rounded-xl font-semibold hover:bg-orange-700 transition-all"
              >
                {showCreateForm ? t.cancel : t.createListing || 'Create Listing'}
              </motion.button>
            </div>

            {showCreateForm && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white/90 border border-amber-100 rounded-3xl p-6 mb-6"
              >
                <h3 className="text-xl font-semibold text-amber-900 mb-4">{t.createListing || 'Create New Listing'}</h3>
                <form onSubmit={handleCreateListingSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block mb-2 font-medium text-amber-900">{t.type || 'Type'}</label>
                    <select
                      name="type"
                      value={newListing.type}
                      onChange={handleCreateListingChange}
                      className="w-full px-4 py-3 border border-amber-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-400 transition-all bg-white/70 text-amber-900"
                    >
                      <option value="hotel">{t.hotel || 'Hotel'}</option>
                      <option value="circuit">{t.circuit || 'Circuit'}</option>
                      <option value="car">{t.car || 'Car Rental'}</option>
                      <option value="tour">{t.tour || 'Guided Tour'}</option>
                    </select>
                  </div>
                  <div>
                    <label className="block mb-2 font-medium text-amber-900">{t.title || 'Title'}</label>
                    <input
                      type="text"
                      name="title"
                      value={newListing.title}
                      onChange={handleCreateListingChange}
                      required
                      className="w-full px-4 py-3 border border-amber-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-400 transition-all bg-white/70 text-amber-900"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block mb-2 font-medium text-amber-900">{t.description || 'Description'}</label>
                    <textarea
                      name="description"
                      value={newListing.description}
                      onChange={handleCreateListingChange}
                      required
                      className="w-full px-4 py-3 border border-amber-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-400 transition-all bg-white/70 text-amber-900"
                      rows="4"
                    ></textarea>
                  </div>
                  <div>
                    <label className="block mb-2 font-medium text-amber-900">{t.price || 'Price (€)'}</label>
                    <input
                      type="number"
                      name="price"
                      value={newListing.price}
                      onChange={handleCreateListingChange}
                      required
                      min="0"
                      className="w-full px-4 py-3 border border-amber-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-400 transition-all bg-white/70 text-amber-900"
                    />
                  </div>
                  <div>
                    <label className="block mb-2 font-medium text-amber-900">{t.location || 'Location'}</label>
                    <input
                      type="text"
                      name="location"
                      value={newListing.location}
                      onChange={handleCreateListingChange}
                      required
                      className="w-full px-4 py-3 border border-amber-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-orange-400 transition-all bg-white/70 text-amber-900"
                    />
                  </div>
                  <div className="md:col-span-2 flex justify-end">
                    <motion.button
                      type="submit"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      className="py-3 px-6 bg-orange-600 text-white rounded-xl font-semibold hover:bg-orange-700 transition-all"
                    >
                      {t.create || 'Create'}
                    </motion.button>
                  </div>
                </form>
              </motion.div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {listings.length === 0 ? (
                <div className="col-span-full text-center text-amber-900/80">
                  {t.noListings || 'No listings found. Create one to get started.'}
                </div>
              ) : (
                listings.map((listing) => (
                  <motion.div
                    key={listing.id}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3 }}
                    className="bg-white/90 border border-amber-100 rounded-3xl shadow-xl p-6"
                  >
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-xl font-serif font-semibold text-amber-900">{listing.title}</h3>
                      <span
                        className={`text-sm font-semibold ${
                          listing.status === 'published' ? 'text-green-600' : 'text-gray-600'
                        }`}
                      >
                        {listing.status === 'published' ? t.published : t.draft}
                      </span>
                    </div>
                    <div className="text-amber-900/80 mb-4">
                      <p className="flex items-center">
                        <Briefcase className="h-5 w-5 mr-2 text-orange-600" />
                        {t[listing.type] || listing.type}
                      </p>
                      <p className="flex items-center">
                        <MapPin className="h-5 w-5 mr-2 text-orange-600" />
                        {listing.location}
                      </p>
                      <p className="flex items-center">
                        <Users className="h-5 w-5 mr-2 text-orange-600" />
                        {listing.bookings_count || 0} {t.bookings || 'bookings'}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <motion.button
                        onClick={() => handlePublishToggle(listing.id, listing.status)}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="flex-1 py-2 px-4 bg-amber-100 text-amber-900 rounded-xl font-semibold hover:bg-amber-200 transition-all"
                      >
                        {listing.status === 'published' ? t.unpublish : t.publish}
                      </motion.button>
                      <motion.button
                        onClick={() => navigate(`/pro/edit-listing/${listing.id}`)}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="p-2 bg-orange-600 text-white rounded-xl hover:bg-orange-700 transition-all"
                      >
                        <Edit className="h-5 w-5" />
                      </motion.button>
                      <motion.button
                        onClick={() => handleDeleteListing(listing.id)}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="p-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-all"
                      >
                        <Trash2 className="h-5 w-5" />
                      </motion.button>
                    </div>
                  </motion.div>
                ))
              )}
            </div>
          </motion.div>
        )}

        {/* Bookings Tab */}
        {activeTab === 'bookings' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="bg-white/90 border border-amber-100 rounded-3xl p-6"
          >
            <h2 className="text-2xl font-serif font-semibold text-amber-900 mb-6">{t.bookings || 'Bookings'}</h2>
            {bookings.length === 0 ? (
              <div className="text-center text-amber-900/80">{t.noBookings || 'No bookings found.'}</div>
            ) : (
              <div className="space-y-4">
                {bookings.map((booking) => (
                  <div
                    key={booking.id}
                    className="border border-amber-100 rounded-xl p-4 bg-orange-50/50"
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <h3 className="text-lg font-semibold text-amber-900">{booking.listing_title}</h3>
                        <p className="text-amber-900/80">{t.client || 'Client'}: {booking.client_name}</p>
                        <p className="text-amber-900/80">
                          {t.date || 'Date'}: {format(new Date(booking.date), 'PPP', { locale: language === 'fr' ? fr : undefined })}
                        </p>
                        <p className="text-amber-900/80">{t.total || 'Total'}: {booking.total_price} €</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span
                          className={`text-sm font-semibold ${
                            booking.status === 'confirmed'
                              ? 'text-green-600'
                              : booking.status === 'pending'
                              ? 'text-yellow-600'
                              : 'text-red-600'
                          }`}
                        >
                          {t[booking.status] || booking.status}
                        </span>
                        <select
                          value={booking.status}
                          onChange={(e) => handleBookingStatusUpdate(booking.id, e.target.value)}
                          className="px-2 py-1 border border-amber-200 rounded-xl bg-white/70 text-amber-900"
                        >
                          <option value="pending">{t.pending || 'Pending'}</option>
                          <option value="confirmed">{t.confirmed || 'Confirmed'}</option>
                          <option value="canceled">{t.canceled || 'Canceled'}</option>
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* Messages Tab */}
        {activeTab === 'messages' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="bg-white/90 border border-amber-100 rounded-3xl p-6"
          >
            <h2 className="text-2xl font-serif font-semibold text-amber-900 mb-6">{t.messages || 'Messages'}</h2>
            {messages.length === 0 ? (
              <div className="text-center text-amber-900/80">{t.noMessages || 'No messages found.'}</div>
            ) : (
              <div className="space-y-4">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`border border-amber-100 rounded-xl p-4 ${
                      message.status === 'unread' ? 'bg-orange-100/50' : 'bg-orange-50/50'
                    }`}
                  >
                    <div className="flex justify-between items-center">
                      <div>
                        <h3 className="text-lg font-semibold text-amber-900">{message.subject}</h3>
                        <p className="text-amber-900/80">{t.from || 'From'}: {message.client_name}</p>
                        <p className="text-amber-900/80">
                          {format(new Date(message.date), 'PPP', { locale: language === 'fr' ? fr : undefined })}
                        </p>
                        <p className="text-amber-900/80 mt-2">{message.message}</p>
                      </div>
                      {message.status === 'unread' && (
                        <motion.button
                          onClick={() => handleMarkMessageRead(message.id)}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className="p-2 bg-orange-600 text-white rounded-xl hover:bg-orange-700 transition-all"
                        >
                          <Mail className="h-5 w-5" />
                        </motion.button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="bg-white/90 border border-amber-100 rounded-3xl p-6"
          >
            <h2 className="text-2xl font-serif font-semibold text-amber-900 mb-6">{t.analytics || 'Analytics'}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-orange-50/50 border border-amber-100 rounded-xl p-4 text-center">
                <BarChart2 className="h-8 w-8 mx-auto text-orange-600 mb-2" />
                <h3 className="text-lg font-semibold text-amber-900">{t.totalBookings || 'Total Bookings'}</h3>
                <p className="text-2xl font-bold text-orange-600">{analytics.totalBookings}</p>
              </div>
              <div className="bg-orange-50/50 border border-amber-100 rounded-xl p-4 text-center">
                <Briefcase className="h-8 w-8 mx-auto text-orange-600 mb-2" />
                <h3 className="text-lg font-semibold text-amber-900">{t.revenue || 'Revenue'}</h3>
                <p className="text-2xl font-bold text-orange-600">{analytics.revenue?.total || 0} €</p>
              </div>
              <div className="bg-orange-50/50 border border-amber-100 rounded-xl p-4 text-center">
                <Calendar className="h-8 w-8 mx-auto text-orange-600 mb-2" />
                <h3 className="text-lg font-semibold text-amber-900">{t.pendingBookings || 'Pending Bookings'}</h3>
                <p className="text-2xl font-bold text-orange-600">{analytics.pendingBookings}</p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Booking Process Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.6 }}
          className="mt-16"
        >
          <div className="text-center mb-12">
            <span className="inline-block px-5 py-2 rounded-full text-sm font-semibold mb-6 border backdrop-blur-md border-orange-400/30 bg-amber-100/50 text-amber-700">
              {t.proProcessTitle || 'How It Works'}
            </span>
            <h2 className="text-3xl font-serif font-extrabold mb-4 text-amber-900">
              {t.proProcess || 'Manage Your Business with Ease'}
            </h2>
            <p className="text-lg max-w-2xl mx-auto leading-relaxed text-amber-900/80">
              {t.proProcessDesc || 'Follow these simple steps to grow your travel business in Morocco.'}
            </p>
          </div>

          <div className="relative">
            <div
              className="absolute left-1/2 transform -translate-x-1/2 h-full w-1 z-0"
              style={{ background: 'linear-gradient(to bottom, rgba(249, 115, 22, 0.5), #F97316, rgba(249, 115, 22, 0.5))' }}
            ></div>
            <div className="relative z-10 space-y-24">
              {bookingSteps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="flex flex-col md:flex-row items-center relative"
                >
                  {index % 2 === 0 ? (
                    <>
                      <div className="md:w-1/2 md:pr-16 mb-8 md:mb-0 text-right">
                        <h3 className="text-2xl font-serif font-semibold mb-3 text-amber-900">
                          {step.title}
                        </h3>
                        <p className="leading-relaxed text-amber-900/80">{step.description}</p>
                      </div>
                      <div
                        className="backdrop-blur-md rounded-full p-5 z-10 shadow-xl border border-amber-100"
                        style={{ backgroundColor: 'rgba(255, 247, 237, 0.9)', color: '#F97316' }}
                      >
                        {step.icon}
                      </div>
                      <div className="md:w-1/2 md:pl-16 hidden md:block"></div>
                    </>
                  ) : (
                    <>
                      <div className="md:w-1/2 md:pr-16 hidden md:block"></div>
                      <div
                        className="backdrop-blur-md rounded-full p-5 z-10 shadow-xl border border-amber-100"
                        style={{ backgroundColor: 'rgba(255, 247, 237, 0.9)', color: '#F97316' }}
                      >
                        {step.icon}
                      </div>
                      <div className="md:w-1/2 md:pl-16 mb-8 md:mb-0 text-left">
                        <h3 className="text-2xl font-serif font-semibold mb-3 text-amber-900">
                          {step.title}
                        </h3>
                        <p className="leading-relaxed text-amber-900/80">{step.description}</p>
                      </div>
                    </>
                  )}
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default ProSection;